import React, { useState, useRef, useCallback } from 'react';
import { Check, Flame, Calendar as CalendarIcon, FileText, Gift, Trophy, FileImage, Clock, Repeat, Infinity, AlertCircle, Archive } from 'lucide-react';
import { Habit } from '../types';
import { AVAILABLE_ICONS } from '../constants';
import { format, differenceInCalendarDays, subDays } from 'date-fns';
import { WeekCircle, getColorHex } from './WeekCircle';

interface HabitCardProps {
  habit: Habit;
  currentTime?: Date;
  onToggle: (id: string, date: string) => void;
  onEdit: (habit: Habit) => void;
  onLog: (habit: Habit, date: string) => void;
  onCheckInAndLog?: (habit: Habit, date: string) => void;
  onArchive?: (id: string) => void;
}

const getFrequencyLabel = (habit: Habit) => {
    switch (habit.frequency) {
        case 'daily': return '每天';
        case 'weekly': return '每周';
        case 'interval': return `每 ${habit.intervalDays || 2} 天`;
        case 'workdays': return '工作日'; // legacy
        case 'custom': return '自定义'; // legacy
        default: return '每天';
    }
}

// Logic to check if habit is "due" or "active" on a specific day
const isHabitTargetDay = (habit: Habit, date: Date) => {
    if (habit.frequency === 'daily') return true;
    if (habit.frequency === 'weekly' || habit.frequency === 'workdays' || habit.frequency === 'custom') {
        return habit.targetDays.includes(date.getDay());
    }
    if (habit.frequency === 'interval') {
        const interval = habit.intervalDays || 2;
        // Logic: active if (today - lastCompletedDay) >= interval
        // OR simply display it as "active" always, but status depends on last checkin?
        // Standard habit trackers often treat "every X days" as valid everyday until done, then strictly due on Xth day.
        // For visualization (heatmap), usually every day is a "potential" day, but strictly:
        
        // Simpler approach for "Target Day" visualization (dots):
        // If completed: it was a target.
        // If not completed:
        //   If no history: True (due immediately)
        //   If history: is date >= lastCompleted + interval?
        
        // However, for the mini-graph, calculating this dynamically for past days is complex without full history replay.
        // Simplified fallback for visual graph: Treat as always active (target) so user can check in any time? 
        // Or only show target dot if it matches the cycle?
        // Let's stick to: Always show as "available" for interval habits in the graph, unless completed.
        return true; 
    }
    return false;
};

// Check if currently due (for Red Dot / Overdue logic)
const checkIsOverdue = (habit: Habit, todayStr: string, currentTimeStr: string) => {
    const isCompletedToday = habit.completedDates.includes(todayStr);
    if (isCompletedToday) return false;
    
    // Check reminder time first
    if (!habit.reminderTime || habit.reminderTime > currentTimeStr) return false;

    // Check if it should be done today
    if (habit.frequency === 'interval') {
        const interval = habit.intervalDays || 2;
        const lastDateStr = [...habit.completedDates].sort().pop();
        
        if (!lastDateStr) return true; // Never done, due today
        
        const lastDate = new Date(lastDateStr);
        const today = new Date(); // Use today's date
        const diff = differenceInCalendarDays(today, lastDate);
        return diff >= interval;
    } else {
        // Daily / Weekly
        const todayDay = new Date().getDay();
        return habit.targetDays.includes(todayDay);
    }
}

// Custom animation styles
const animationStyles = `
  @keyframes soft-shake {
    0% { transform: translateX(0) scale(1); }
    25% { transform: translateX(2px) rotate(1deg) scale(1.02); }
    50% { transform: translateX(-2px) rotate(-1deg) scale(1.02); }
    75% { transform: translateX(1px) rotate(0.5deg) scale(1.01); }
    100% { transform: translateX(0) scale(1); }
  }
  .animate-soft-shake {
    animation: soft-shake 0.3s ease-out;
  }
`;

export const HabitCard: React.FC<HabitCardProps> = ({ habit, currentTime, onToggle, onEdit, onLog, onCheckInAndLog, onArchive }) => {
  const [isShaking, setIsShaking] = useState(false);
  const [showUncheckConfirm, setShowUncheckConfirm] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const lastTap = useRef<number>(0);

  const isTouchDevice = typeof window !== 'undefined' && 
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const handleCardClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (isTouchDevice) {
      const now = Date.now();
      const delta = now - lastTap.current;
      lastTap.current = now;
      if (delta < 300 && delta > 0) {
        onEdit(habit);
      }
    } else {
      onEdit(habit);
    }
  }, [isTouchDevice, onEdit, habit]);
  
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  const isCompletedToday = habit.completedDates.includes(todayStr);

  // Period Logic
  const isPermanent = habit.isPermanent ?? true;
  const hasStartDate = habit.startDate && new Date(habit.startDate) > today;
  const hasEnded = habit.endDate && new Date(habit.endDate) < today;
  const isExpired = !isPermanent && hasEnded;
  const isPending = !isPermanent && hasStartDate;
  
  const getPeriodLabel = () => {
    if (isPermanent) return null;
    if (habit.startDate && habit.endDate) {
      const start = format(new Date(habit.startDate), 'MM/dd');
      const end = format(new Date(habit.endDate), 'MM/dd');
      return `${start} - ${end}`;
    }
    return null;
  };
  const periodLabel = getPeriodLabel();

  // Reminder Logic
  const nowStr = format(currentTime || new Date(), 'HH:mm');
  const hasReminder = !!habit.reminderTime;
  const isOverdue = hasReminder && checkIsOverdue(habit, todayStr, nowStr);

  // Check if there is a log for today
  const dailyLog = habit.logs && habit.logs.find(l => l.date === todayStr);
  const hasLogToday = !!dailyLog;
  const hasImage = !!dailyLog?.image;

  const IconComponent = AVAILABLE_ICONS.find(i => i.name === habit.icon)?.component || AVAILABLE_ICONS[0].component;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isExpired || isPending) return;
    
    // If already completed, show confirmation to uncheck
    if (isCompletedToday) {
      setShowUncheckConfirm(true);
      return;
    }
    
    // 1. Tactile Feedback (Vibration)
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(50);
    }

    // 2. Visual Feedback (Shake Animation)
    if (!isShaking) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 300);
    }

    // 3. Auto-open log modal
    if (onCheckInAndLog) {
      onCheckInAndLog(habit, todayStr);
      return;
    }

    onToggle(habit.id, todayStr);
  };

  const handleConfirmUncheck = () => {
    onToggle(habit.id, todayStr);
    setShowUncheckConfirm(false);
  };

  const handleLogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLog(habit, todayStr);
  };

  // --- Milestone Logic ---
  const sortedMilestones = (habit.milestones || []).sort((a, b) => a.target - b.target);
  
  const nextMilestone = sortedMilestones.find(m => m.target > habit.streak);
  const achievedMilestones = sortedMilestones.filter(m => m.target <= habit.streak);
  const lastAchievedMilestone = achievedMilestones.length > 0 ? achievedMilestones[achievedMilestones.length - 1] : null;

  let progress = 0;
  let isAllAchieved = false;

  if (nextMilestone) {
    progress = Math.min((habit.streak / nextMilestone.target) * 100, 100);
  } else if (sortedMilestones.length > 0) {
    isAllAchieved = true;
    progress = 100;
  }

  const isJustAchieved = lastAchievedMilestone?.target === habit.streak;

  return (
    <>
    <style>{animationStyles}</style>
    <div 
      ref={cardRef}
      onClick={handleCardClick}
      className={`
        bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-700 
        hover:shadow-md transition-all ${isTouchDevice ? '' : 'cursor-pointer group'} relative overflow-hidden
        ${isJustAchieved ? 'ring-2 ring-yellow-400 dark:ring-yellow-500' : ''}
        ${isExpired ? 'opacity-50' : ''}
        ${isPending ? 'opacity-70' : ''}
        ${isShaking ? 'animate-soft-shake' : ''}
      `}
    >
      {/* Milestone Badge */}
      {isJustAchieved && (
        <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-2 py-1 rounded-bl-lg z-10 flex items-center animate-pulse">
          <Trophy size={10} className="mr-1" /> 达成: {lastAchievedMilestone?.target}天
        </div>
      )}
      
      {isAllAchieved && !isJustAchieved && (
         <div className="absolute top-0 right-0 bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 text-[10px] font-bold px-2 py-1 rounded-bl-lg z-10 flex items-center">
          <Trophy size={10} className="mr-1" /> 大满贯
        </div>
      )}

      {/* Period Badges */}
      {isExpired && (
        <div className="absolute top-0 left-0 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-br-lg z-10 flex items-center">
          <AlertCircle size={10} className="mr-1" /> 已结束
        </div>
      )}
      
      {isPending && (
        <div className="absolute top-0 left-0 bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded-br-lg z-10 flex items-center">
          <Clock size={10} className="mr-1" /> 待开始
        </div>
      )}

      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-4 flex-1">
          <button
            onClick={handleToggle}
            className="relative w-16 h-16 flex items-center justify-center flex-shrink-0 transition-all duration-300 transform active:scale-90 cursor-pointer"
          >
            <WeekCircle completedDates={habit.completedDates} color={habit.color} size={56} />
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <IconComponent size={40} />
            </div>
            {isCompletedToday && !isExpired && !isPending && (
              <div className="absolute flex items-center justify-center bg-green-500/80 rounded-full" style={{ width: 48, height: 48 }}>
                <Check size={24} strokeWidth={3} className="text-white animate-bounce" />
              </div>
            )}
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg flex items-center gap-2 truncate" style={{ color: getColorHex(habit.color) }}>
              {habit.title}
            </h3>
            {habit.description && (
              <p className="text-slate-500 dark:text-slate-400 text-sm truncate">{habit.description}</p>
            )}
            
            <div className="flex flex-wrap items-center mt-2 gap-3 text-xs text-slate-400 font-medium">
              <span className="flex items-center text-orange-500">
                <Flame size={12} className="mr-1" fill="currentColor" />
                {habit.streak} {nextMilestone ? `/ ${nextMilestone.target}` : ''} 天
              </span>
              <span className="flex items-center">
                 {habit.frequency === 'interval' ? <Repeat size={12} className="mr-1" /> : <CalendarIcon size={12} className="mr-1" />}
                 {getFrequencyLabel(habit)}
              </span>
              
              {/* Period Label */}
              {periodLabel && (
                <span className="flex items-center text-cyan-500">
                  {isPermanent ? <Infinity size={12} className="mr-1" /> : <CalendarIcon size={12} className="mr-1" />}
                  {periodLabel}
                </span>
              )}
              
              {/* Reminder Indicator */}
              {hasReminder && (
                  <span className={`flex items-center ${isOverdue ? 'text-red-500 font-bold' : 'text-slate-400'}`}>
                      <Clock size={12} className="mr-1" />
                      {habit.reminderTime}
                      {isOverdue && <span className="ml-1 text-[10px] bg-red-100 dark:bg-red-900/30 px-1 rounded">超时</span>}
                  </span>
              )}
            </div>
            
            {/* Reward Text Logic */}
            {(nextMilestone || (isAllAchieved && lastAchievedMilestone)) && (
              <div className="mt-1 flex items-center text-xs text-indigo-500 dark:text-indigo-400 truncate">
                <Gift size={10} className="mr-1 flex-shrink-0" />
                <span className="truncate">
                    {isJustAchieved 
                        ? `获得奖励: ${lastAchievedMilestone?.reward}` 
                        : nextMilestone 
                            ? `目标: ${nextMilestone.reward}`
                            : `达成: ${lastAchievedMilestone?.reward}`}
                </span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-3 pl-2">
            {/* Log/Detail Button - always visible on touch, hover-only on desktop */}
            {(isCompletedToday || hasLogToday) && (
              <button 
                 onClick={handleLogClick}
                 className={`
                   flex items-center justify-center w-8 h-8 rounded-full transition-all
                   ${hasLogToday 
                     ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-300 ring-2 ring-indigo-50 dark:ring-slate-800' 
                     : 'bg-slate-50 text-slate-400 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700'}
                   ${isTouchDevice ? '' : 'opacity-0 group-hover:opacity-100'}
                 `}
                 title="心得/图片"
              >
                {hasImage ? <FileImage size={14} /> : <FileText size={14} />}
              </button>
           )}

           {/* Archive Button (only for permanent habits) - always visible on touch, hover-only on desktop */}
           {isPermanent && onArchive && (
              <button 
                 onClick={(e) => { e.stopPropagation(); onArchive(habit.id); }}
                 className={`flex items-center justify-center w-8 h-8 rounded-full bg-slate-50 text-slate-300 dark:bg-slate-800 dark:text-slate-600 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-all
                   ${isTouchDevice ? '' : 'opacity-0 group-hover:opacity-100'}
                 `}
                 title="归档结束"
              >
                <Archive size={14} />
              </button>
           )}
        </div>
      </div>
      
    </div>

    {/* Uncheck Confirmation Dialog */}
    {showUncheckConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-2xl max-w-xs w-full animate-scale-in">
          <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">取消打卡？</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">确定要取消「{habit.title}」今天的打卡吗？</p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowUncheckConfirm(false)}
              className="flex-1 py-2.5 text-slate-500 font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleConfirmUncheck}
              className="flex-1 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-colors"
            >
              确认取消
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};